// App.js
import React, { useEffect, useState, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";

import AdminPanel from "./util/AdminPanel";
import PendingEvents from "./pages/PendingEvents";
import HostCodeManagement from "./pages/HostCodeManagement";
import AuthCallback from "./pages/AuthCallback";
import SilentRenew from "./pages/SilentRenew";
import ProtectedRoute from "./auth/protected-route";
import Monitoring from "./pages/monitoring/MonitoringPage";
import ArtistManagement from "./pages/ArtistManagement";
import AdvertisementAdmin from "./pages/AdvertisementAdmin";
import AdUploadForm from "./pages/AdUploadForm";
import PublicReportPage from "./pages/PublicReportPage";
import IssueKanban from "./pages/IssueKanban";
import DataQuality from "./pages/data-quality/DataQuality";
import ProfilePictures from "./pages/ProfilePictures";
import ImageRights from "./pages/image-rights/ImageRights";
import TeamPlan from "./pages/team-plan/TeamPlan";
import Material from "./pages/material/Material";

import { logout, isAuthenticated, subscribeAuthChanges } from "./auth/auth";
import { userManager } from "./auth/oidc";
import Sidebar from "./layout/Sidebar";
import useTaskCounts from "./layout/useTaskCounts";
import { MenuIcon } from "./layout/icons";
import Overview from "./pages/overview/Overview";

import EventCreation from "./pages/dashboard/components/EventCreation";
import ClubCreation from "./pages/dashboard/components/ClubCreation";

import "./App.css";

// The advertisement feature is switched off in the backend (schick.advertisements.enabled);
// its pages stay hidden unless the build turns them back on.
const ADVERTISING_ENABLED = process.env.REACT_APP_ADVERTISING_ENABLED === 'true';

/**
 * The signed-in frame: a sidebar grouped by what one does, with what is waiting counted, and the
 * page beside it. On a phone the sidebar is a sheet behind the menu button.
 */
function AppShell() {
  const location = useLocation();
  const [authed, setAuthed] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [userName, setUserName] = useState('');
  const [navOpen, setNavOpen] = useState(false);

  const refreshAuth = useCallback(async () => {
    try {
      const ok = await isAuthenticated();
      setAuthed(ok);
      const user = ok ? await userManager.getUser() : null;
      const profile = user?.profile || {};
      setUserName(profile.given_name || profile.name || profile.preferred_username || '');
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => { refreshAuth(); }, [refreshAuth]);

  // react to OIDC events (login/logout/renew/expired)
  useEffect(() => {
    const unsub = subscribeAuthChanges(() => refreshAuth());
    return unsub;
  }, [refreshAuth]);

  // re-check on route changes (z.B. nach /auth/callback), and close the phone's menu
  useEffect(() => {
    refreshAuth();
    setNavOpen(false);
  }, [location.pathname, refreshAuth]);

  // No frame until the sign-in is known, so nothing flickers.
  const showNav = authChecked && authed;
  const counts = useTaskCounts(showNav, location.pathname);

  return (
    <div className={'shell' + (showNav ? ' shell--signed-in' : '') + (navOpen ? ' is-nav-open' : '')}>
      {showNav && (
        <>
          <header className="topbar">
            <button type="button" className="topbar__menu" aria-label="Menü öffnen" onClick={() => setNavOpen(true)}>
              <MenuIcon />
            </button>
            <span className="topbar__title">Schick Admin</span>
          </header>
          <Sidebar
            counts={counts}
            advertising={ADVERTISING_ENABLED}
            userName={userName}
            onLogout={logout}
            onNavigate={() => setNavOpen(false)}
          />
          <div className="sidebar-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />
        </>
      )}

      <main className="app-main">
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/silent-renew" element={<SilentRenew />} />

          <Route path="/" element={<ProtectedRoute><Overview counts={counts} userName={userName} /></ProtectedRoute>} />
          <Route path="/events" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
          <Route path="/pending-events" element={<ProtectedRoute><PendingEvents /></ProtectedRoute>} />
          <Route path="/data-quality" element={<ProtectedRoute><DataQuality /></ProtectedRoute>} />
          <Route path="/host-codes" element={<ProtectedRoute><HostCodeManagement /></ProtectedRoute>} />
          <Route path="/create-club" element={<ProtectedRoute><ClubCreation /></ProtectedRoute>} />
          <Route path="/manage-events" element={<ProtectedRoute><EventCreation /></ProtectedRoute>} />
          <Route path="/monitoring" element={<ProtectedRoute><Monitoring /></ProtectedRoute>} />
          <Route path="/artists" element={<ProtectedRoute><ArtistManagement /></ProtectedRoute>} />
          {ADVERTISING_ENABLED && <Route path="/advertisement" element={<ProtectedRoute><AdvertisementAdmin /></ProtectedRoute>} />}
          <Route path="/issues" element={<ProtectedRoute><IssueKanban /></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute><TeamPlan /></ProtectedRoute>} />
          <Route path="/material" element={<ProtectedRoute><Material /></ProtectedRoute>} />
          <Route path="/profile-pictures" element={<ProtectedRoute><ProfilePictures /></ProtectedRoute>} />
          <Route path="/image-rights" element={<ProtectedRoute><ImageRights /></ProtectedRoute>} />
          {ADVERTISING_ENABLED && <Route path="/ad-upload/:token" element={<AdUploadForm />} />}
          {ADVERTISING_ENABLED && <Route path="/advertisements/reports/:token" element={<PublicReportPage />} />}
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}
